import {
  DsdFileStatus,
  type GetTaskStatusResponse,
  type TaskStatusValue,
} from "@/types/dsd";

/** XBRL 분석 태스크 상태 파생 — 순수 함수만 둔다.
 *
 * hooks.ts 에 두면 테스트가 API 모듈 → axios-instance → firebase 를 끌고 와서,
 * 환경변수가 없는 CI 에서 모듈 로드 시점에 throw 한다. */

export const getSheetIdToStatusMap = (
  response: GetTaskStatusResponse,
): Record<string, TaskStatusValue> =>
  Object.fromEntries(
    response.sheets.map((sheet) => [sheet.sheetId, sheet.status]),
  );

/** 시트가 셀 단위 태깅(오토태깅·롤포워드) 대상인지. status 와 별개의 축이다 —
 * 이미 분석된 일반사항·회계정책·추정 목차은 COMPLETED 인데도 태깅 대상이 아니다. */
export const getSheetIdToTaggingTargetMap = (
  response: GetTaskStatusResponse,
): Record<string, boolean> =>
  Object.fromEntries(
    response.sheets.map((sheet) => [sheet.sheetId, sheet.taggingTarget]),
  );

/** 아직 워크북이 확정 전이라 그 시트의 편집·조회를 막아야 하는 상태.
 *
 * 진행 중 판정은 전부 이 함수를 본다 — 백엔드가 새 진행 중 상태를 추가하면
 * 여기 한 곳만 고치면 편집 차단·스피너·분석 중 화면이 함께 따라온다. 열거를
 * 판정 지점마다 반복하면 새 상태가 그 지점들을 조용히 통과한다(ADJUSTING_ROLE
 * 이 실제로 그랬다). */
export type SheetAnalyzingStatus = "RUNNING" | "PENDING" | "ADJUSTING_ROLE";

export const isSheetAnalyzing = (
  status: TaskStatusValue | null | undefined,
): status is SheetAnalyzingStatus =>
  status === "RUNNING" || status === "PENDING" || status === "ADJUSTING_ROLE";

/** 분석이 더 이상 진행 중이 아닌지 (RUNNING, PENDING 없음).
 *
 * NOT_TARGET 은 COMPLETED 와 같이 "더 진행될 것이 없는" 상태다. 여기서 빠지면 본문
 * 재무제표 시트가 항상 NOT_TARGET 이라 모든 워크북의 폴링이 멈추지 않는다. */
export const checkAnalyzingFinished = (response: GetTaskStatusResponse) => {
  return response.sheets.every(
    (sheet) =>
      sheet.status === "COMPLETED" ||
      sheet.status === "FAILED" ||
      sheet.status === "NOT_TARGET",
  );
};

/** 워크북 전체 대상 액션(전체 오토태깅·전체 표 불러오기)을 실행해도 되는지.
 *
 * 워크북 status 가 XBRL_ANALYZED 여도 개별 시트는 아직 분석 중일 수 있고, 그 시트의
 * 표는 전체 태깅에서 조용히 빠진다(WXBRL-2682). 두 축을 함께 보는 판정은 여기 한 곳만
 * 둔다 — 오토태깅 쪽과 롤포워드 쪽이 각자 조립하다가 서로 달라진 것이 이 버그였다.
 *
 * 시트 단위 액션은 이 판정을 쓰지 않는다. 섞으면 딴 시트가 분석 중일 때 현재 표가
 * 잠기는 회귀가 난다(WXBRL-2182).
 *
 * 모르는 상태는 전부 "실행 불가"로 본다(fail-closed) — 되돌릴 수 없는 덮어쓰기라서
 * 잘못 열리는 쪽이 잘못 잠기는 쪽보다 비싸다. 둘로 갈린다:
 * - taskStatus 미수신(로딩·실패)이면 불가. 조회용 `useIsWorkbookAnalyzing` 은 반대로
 *   fail-open 인데, 그 계약은 조회를 영영 막지 않으려는 것이라 실행 게이트와 다르다.
 *   `useDsdTaskStatus` 가 gcTime/staleTime 0 이라 진입 직후 빈 구간이 실제로 생긴다.
 * - 종료 상태 허용 목록(`checkAnalyzingFinished`)으로 판정한다. `isSheetAnalyzing` 의
 *   진행 중 열거를 쓰지 않는 이유도 같다 — 백엔드가 새 상태를 추가하면 허용 목록 쪽은
 *   "아직 안 끝남"으로 보고 잠그지만, 진행 중 열거 쪽은 모르는 상태를 통과시킨다. */
export const canRunWorkbookWideAction = (
  workbookStatus: DsdFileStatus | undefined,
  taskStatus: GetTaskStatusResponse | undefined,
): boolean =>
  workbookStatus === DsdFileStatus.XBRL_ANALYZED &&
  !!taskStatus &&
  checkAnalyzingFinished(taskStatus);
